import React, { useEffect } from "react";
import Grid from "@mui/material/Grid2";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  AccordionDetailsStyle,
  AccordionStyle,
  AccordionSummaryStyle,
  AnchorTagStyle,
  AuthContainer,
  ContactGrid,
  ContactInfoTableCell,
  EmailLink,
  FaqsHeader,
  GridContainer,
  HeaderTableCell,
  InformationGrid,
  QuestionButton,
  SpanStyle,
} from "../styles/style";
import { ContactInfoHeaderLabel } from "../../../constants/Faqs";
import { getCurrnetPageFullPath, getUaserPageViewPayload, getValue, length } from "../../../utils";
import {
  ContactInformation,
  FrequentlyAskedQuestion,
  QuestionData,
} from "../../../models/Faq";
import { UserPageTrack } from "../../../models/User";

import { ApplicationTitles } from "../../../constants/HomePage";
import { getValidMessageType } from "../../../constants/User";
import { redirectUri } from "../../../constants/Config";

export interface PropsFromState {
  faqData: FrequentlyAskedQuestion;
}

export interface PropsFromDispatch {
  getFrequentlyAskedQuestion: () => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const Faqs: React.FC<AllProps> = ({ faqData, getFrequentlyAskedQuestion, postUserPageTrack }) => {
  
  
  useEffect(() => {
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.FAQS),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
    getFrequentlyAskedQuestion();
  }, []);

  const header = () => {
    return (
      <Grid size={{ xs: 12 }} sx={FaqsHeader}>
        <Typography
          variant="h1"
          color={colors.white[1]}
          fontWeight={fontWeight[2]}
          fontSize="3.5rem"
          mb={2}
        >
          FA<span style={SpanStyle}>Q</span>
        </Typography>
        <Typography
          variant="h3"
          color={colors.white[1]}
          fontWeight={fontWeight[2]}
        >
          Frequently Asked Questions
        </Typography>
      </Grid>
    );
  };

  const renderTableCell = (value: string | number) => {
    return <TableCell sx={ContactInfoTableCell}>{renderData(value)}</TableCell>;
  };

  return (
    <Box>
      <Grid container sx={GridContainer}>
        <Grid size={{ xs: 12 }} sx={AuthContainer}>
          {header()}
          <Grid size={{ xs: 12 }} sx={ContactGrid}>
            {renderData("Contact Information:")}
          </Grid>
          <Grid size={{ xs: 12 }} sx={ContactGrid}>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    {renderTableCell("Main Line")}
                    {renderTableCell(getValue(faqData, "mainLine"))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid size={{ xs: 12 }} sx={InformationGrid}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {ContactInfoHeaderLabel.map((data: string) => (
                      <TableCell key={data} sx={HeaderTableCell}>
                        <Typography
                          color={colors.portalColor[2]}
                          variant="h4"
                          fontWeight={fontWeight[6]}
                        >
                          {data}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {faqData &&
                    length(faqData.contactInformation) &&
                    faqData.contactInformation.map(
                      (info: ContactInformation) => (
                        <TableRow key={info.contactId}>
                          {renderTableCell(info.department)}
                          <TableCell>
                            <Link
                              href={`mailto:${info.email}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={EmailLink}
                            >
                              {info.email}
                            </Link>
                          </TableCell>
                          {renderTableCell(info.fax)}
                        </TableRow>
                      )
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid size={{ xs: 12 }} sx={InformationGrid}>
            {faqData &&
              length(faqData.questionData) &&
              faqData.questionData.map((item: QuestionData, index: number) => (
                <Accordion key={item.question} sx={AccordionStyle}>
                  <AccordionSummary
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    sx={AccordionSummaryStyle}
                  >
                    <Button sx={QuestionButton} disableRipple>
                      {item.question}
                    </Button>
                  </AccordionSummary>
                  <AccordionDetails sx={AccordionDetailsStyle}>
                    {renderQuestionData(
                      item.answer,
                      faqData.questionData.length - 1 === index
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const renderData = (value: number | string) => {
  return (
    <Typography
      variant="h3"
      color={colors.portalColor[2]}
      fontWeight={fontWeight[2]}
      lineHeight="1.5rem"
    >
      {value}
    </Typography>
  );
};

const renderQuestionData = (value: string, isLinkUnderline: boolean) => {
  return (
    <Typography
      variant="h3"
      color={colors.portalColor[2]}
      fontWeight={fontWeight[2]}
      lineHeight="1.6rem"
      dangerouslySetInnerHTML={{ __html: value }}
      sx={AnchorTagStyle(isLinkUnderline)}
    />
  );
};

export default Faqs;
